import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import LOGO from "../../../assets/img/MOSIDA-logo.png";
import FifaBg from "../../../assets/img/fifa/fifa-inner.png";
import CardPlaceholder from "../../../assets/img/fifa/fifa-placeholder-card.png";
import type { RootState } from "../../../redux/store";
import { generateFifaWorldCupCard } from "../../../services/fifaCardGenerationService";
import { resolveFifaGeneratedCardPreview } from "../../../utils/fifaCardPreview";
import {
  isFifaWorldCup2026Station,
  persistFifaGeneratedCard,
  readActiveStoredStation,
  readFifaPlayerProfile,
  readFifaPlayerStats,
  readFifaSelfie,
  readSelectedFifaCardDesign,
  resolveFifaCapturedImage,
  resolveFifaStationSlug,
  type FifaPlayerProfile,
  type FifaPlayerStats,
} from "../../../utils/stationFlowStorage";
import { readSelectedAiSelfieArtFromStorage } from "../../../services/stylePromptResolver";

const MESSAGE_INTERVAL_MS = 2000;

const CREATING_MESSAGES = [
  { line1: "CREATING YOUR", line2: "2026 WORLD CUP CARD" },
  { line1: "SCOUTING YOUR", line2: "SUPERSTAR LOOK" },
  { line1: "ANALYZING YOUR", line2: "PLAYER PHOTO" },
  { line1: "MATCHING YOUR", line2: "CLUB JERSEY" },
  { line1: "DESIGNING YOUR", line2: "FIFA STYLE CARD" },
  { line1: "ENHANCING", line2: "STATS AND DETAILS" },
  { line1: "BUILDING YOUR", line2: "FOOTBALL IDENTITY" },
] as const;

const CreatingFifaCard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [messageIndex, setMessageIndex] = useState(0);
  const [generatedCardUrl, setGeneratedCardUrl] = useState<string | null>(null);
  const [generationReady, setGenerationReady] = useState(false);
  const generationStartedRef = useRef(false);
  const uploadedImageUrl = useSelector(
    (state: RootState) => state.uploadFlow.uploadedImageUrl
  );

  const stationData = useMemo(() => readActiveStoredStation(), [location.state]);
  const selectedAiSelfieArt = useMemo(
    () => readSelectedAiSelfieArtFromStorage(),
    [location.state]
  );
  const stationSlug = useMemo(
    () => resolveFifaStationSlug(stationData, selectedAiSelfieArt),
    [stationData, selectedAiSelfieArt]
  );
  const isFifaFlow = isFifaWorldCup2026Station(stationData, selectedAiSelfieArt);

  const locationState = location.state as {
    capturedImage?: string;
    stationData?: typeof stationData;
    fifaPlayerProfile?: FifaPlayerProfile;
    fifaPlayerStats?: FifaPlayerStats;
    fifaStatsSource?: "manual" | "ai";
    fifaStatsSkipped?: boolean;
    fifaRetryGeneration?: boolean;
    previousFifaGeneratedCard?: string;
  } | null;

  const capturedImage = resolveFifaCapturedImage({
    locationImage: locationState?.capturedImage,
    reduxImage: uploadedImageUrl,
  });
  const selfieImageUrl = readFifaSelfie() || capturedImage;
  const fifaPlayerProfile =
    locationState?.fifaPlayerProfile || readFifaPlayerProfile();
  const isAiStats =
    locationState?.fifaStatsSource === "ai" || Boolean(locationState?.fifaStatsSkipped);
  const fifaPlayerStats = isAiStats
    ? null
    : locationState?.fifaPlayerStats || readFifaPlayerStats();
  const activeMessage = CREATING_MESSAGES[messageIndex];
  const isRetry = Boolean(locationState?.fifaRetryGeneration);
  const previousCard = locationState?.previousFifaGeneratedCard;

  const sharedRouteState = useMemo(
    () => ({
      capturedImage: selfieImageUrl,
      stationData,
      fifaPlayerProfile,
      fifaPlayerStats,
      fifaStatsSource: locationState?.fifaStatsSource,
      fifaStatsSkipped: locationState?.fifaStatsSkipped,
    }),
    [
      selfieImageUrl,
      stationData,
      fifaPlayerProfile,
      fifaPlayerStats,
      locationState?.fifaStatsSource,
      locationState?.fifaStatsSkipped,
    ]
  );

  useEffect(() => {
    if (!isFifaFlow) {
      navigate(`/${stationSlug}/choose-option`, { replace: true });
      return;
    }

    if (!selfieImageUrl || !fifaPlayerProfile) {
      navigate(`/${stationSlug}/upload-fifa-image`, { replace: true });
    }
  }, [fifaPlayerProfile, isFifaFlow, navigate, selfieImageUrl, stationSlug]);

  useEffect(() => {
    if (!isFifaFlow || !selfieImageUrl || !fifaPlayerProfile || generationStartedRef.current) {
      return;
    }

    generationStartedRef.current = true;

    (async () => {
      const result = await generateFifaWorldCupCard({
        sourceImageUrl: selfieImageUrl,
        profile: fifaPlayerProfile,
        stats: isAiStats ? null : fifaPlayerStats,
        isAiStats,
      });

      if (result.success && result.imageUrl) {
        setGeneratedCardUrl(result.imageUrl);
      } else {
        console.error("[FIFA] Card generation failed:", result.error);
        setGeneratedCardUrl(
          resolveFifaGeneratedCardPreview(readSelectedFifaCardDesign(), isRetry ? 1 : 0)
        );
      }

      setGenerationReady(true);
    })();
  }, [
    fifaPlayerProfile,
    fifaPlayerStats,
    isAiStats,
    isFifaFlow,
    isRetry,
    selfieImageUrl,
  ]);

  useEffect(() => {
    if (generationReady) return;

    const timer = window.setTimeout(() => {
      setMessageIndex((prev) => (prev + 1) % CREATING_MESSAGES.length);
    }, MESSAGE_INTERVAL_MS);

    return () => window.clearTimeout(timer);
  }, [messageIndex, generationReady]);

  useEffect(() => {
    if (!generationReady) return;

    const timer = window.setTimeout(() => {
      const finalCardUrl =
        generatedCardUrl ||
        resolveFifaGeneratedCardPreview(readSelectedFifaCardDesign(), isRetry ? 1 : 0);

      persistFifaGeneratedCard(finalCardUrl);

      if (isRetry && previousCard && previousCard !== finalCardUrl) {
        navigate(`/${stationSlug}/fifa-card-compare`, {
          replace: true,
          state: {
            ...sharedRouteState,
            cardOne: previousCard,
            cardTwo: finalCardUrl,
          },
        });
        return;
      }

      navigate(`/${stationSlug}/save-fifa-card`, {
        replace: true,
        state: {
          ...sharedRouteState,
          fifaGeneratedCard: finalCardUrl,
        },
      });
    }, MESSAGE_INTERVAL_MS);

    return () => window.clearTimeout(timer);
  }, [
    generatedCardUrl,
    generationReady,
    isRetry,
    navigate,
    previousCard,
    sharedRouteState,
    stationSlug,
  ]);

  if (!isFifaFlow || !selfieImageUrl || !fifaPlayerProfile) {
    return null;
  }

  return (
    <section
      className="creatingFifaCardSection"
      style={{ backgroundImage: `url(${FifaBg})` }}
    >
      <div className="creatingFifaCardInner">
        <div className="mosidaLogoWhite creatingFifaCardLogo mb-5">
          <img src={LOGO} alt="MOSIDA logo" className="logo" />
          <span className="badgeCreative">FIFA World Cup</span>
        </div>

        <div className="creatingFifaCardBody">
          <h1
            key={messageIndex}
            className="creatingFifaCardHeading creatingFifaCardHeading--animate"
          >
            <div className="creatingFifaCardHeadingLine">{activeMessage.line1}</div>
            <div className="creatingFifaCardHeadingLine creatingFifaCardHeadingLine--gold">
              {activeMessage.line2}
            </div>
          </h1>

          <div className="creatingFifaCardPreview">
            <img
              src={CardPlaceholder}
              alt="Creating your FIFA World Cup card"
              className="creatingFifaCardPreviewImage"
              draggable={false}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default CreatingFifaCard;
